"use client";

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import {
  SessionStartChooserContext,
  type SessionStartChooserContextValue,
} from "./session-start-chooser-context";

/**
 * State owner for the ONE session-start chooser overlay (the AuthModalProvider
 * idiom): every "Start new session" entry with no team context — landing
 * navbar, sessions modal, session screen — opens THIS instance through
 * `useSessionStartChooser`; none mount a local copy. The overlay itself
 * renders in `SessionStartChooserHost`, mounted beside the team-modal host.
 */
export function SessionStartChooserProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const [isSessionStartChooserOpen, setIsSessionStartChooserOpen] =
    useState(false);

  const openSessionStartChooser = useCallback((): void => {
    setIsSessionStartChooserOpen(true);
  }, []);

  const closeSessionStartChooser = useCallback((): void => {
    setIsSessionStartChooserOpen(false);
  }, []);

  // A ref, not state: the lent opener is a side effect the host invokes, never
  // anything this provider renders. Held in state it would re-render every
  // chooser consumer each time the landing mounts or remounts.
  const openAllTeamsGalleryRef = useRef<(() => void) | null>(null);

  const setOpenAllTeamsGallery = useCallback(
    (openAllTeamsGallery: (() => void) | null): void => {
      openAllTeamsGalleryRef.current = openAllTeamsGallery;
    },
    [],
  );

  const openAllTeamsGallery = useCallback((): void => {
    openAllTeamsGalleryRef.current?.();
  }, []);

  const contextValue = useMemo<SessionStartChooserContextValue>(
    () => ({
      isSessionStartChooserOpen,
      openSessionStartChooser,
      closeSessionStartChooser,
      openAllTeamsGallery,
      setOpenAllTeamsGallery,
    }),
    [
      isSessionStartChooserOpen,
      openSessionStartChooser,
      closeSessionStartChooser,
      openAllTeamsGallery,
      setOpenAllTeamsGallery,
    ],
  );

  return (
    <SessionStartChooserContext.Provider value={contextValue}>
      {children}
    </SessionStartChooserContext.Provider>
  );
}
